/**
 * Spreadsheet-style column resizing for the tracking tables.
 *
 *   <table class="table trk-table" v-trk-resizable="'journeys'"> ... </table>
 *
 * Drag the right edge of any header cell to resize it; double-click an edge to
 * drop back to automatic widths. Widths are remembered per table (the string
 * passed to the directive is the storage key) so they survive a reload.
 *
 * The table keeps its existing automatic layout until the user actually drags,
 * or until saved widths are found. Only then does it switch to `table-layout:
 * fixed`, which is what stops one column's new width from reflowing the rest.
 */

const MIN_COL_WIDTH = 60;
const STORAGE_PREFIX = 'trk.columnWidths.';

function loadWidths(key) {
  if (!key) {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + key);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed : null;
  } catch (e) {
    return null;
  }
}

function saveWidths(key, widths) {
  if (!key) {
    return;
  }
  try {
    window.localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(widths));
  } catch (e) {
    // Storage blocked (private browsing). Resizing still works for this visit.
  }
}

function clearWidths(key) {
  if (!key) {
    return;
  }
  try {
    window.localStorage.removeItem(STORAGE_PREFIX + key);
  } catch (e) {
    // nothing to do
  }
}

function headerCells(table) {
  const row = table.querySelector('thead tr');
  return row ? Array.prototype.slice.call(row.children) : [];
}

function measure(table) {
  return headerCells(table).map((th) => Math.round(th.getBoundingClientRect().width));
}

/**
 * Pins each column to an explicit width. The inline width/min-width also
 * override `.trk-table-wide`'s min-width, which would otherwise stretch the
 * columns back out whenever the total came to less than its 1360px floor.
 */
function applyWidths(table, widths) {
  const cells = headerCells(table);
  let total = 0;
  cells.forEach((th, index) => {
    const width = widths[index];
    if (width) {
      th.style.width = width + 'px';
      total += width;
    } else {
      total += Math.round(th.getBoundingClientRect().width);
    }
  });
  table.style.tableLayout = 'fixed';
  table.classList.add('trk-table-resized');
  if (total) {
    table.style.width = total + 'px';
    table.style.minWidth = total + 'px';
  }
}

function resetWidths(table) {
  headerCells(table).forEach((th) => {
    th.style.width = '';
  });
  table.style.tableLayout = '';
  table.style.width = '';
  table.style.minWidth = '';
  table.classList.remove('trk-table-resized');
}

function startDrag(event, table, key, index) {
  event.preventDefault();
  event.stopPropagation();

  // First drag on a fresh table: freeze the current automatic layout so the
  // other columns stay exactly where they are while this one changes.
  if (table.style.tableLayout !== 'fixed') {
    applyWidths(table, measure(table));
  }

  const th = headerCells(table)[index];
  if (!th) {
    return;
  }

  const startX = event.pageX;
  const startWidth = th.getBoundingClientRect().width;
  const startTableWidth = table.getBoundingClientRect().width;
  const handle = event.currentTarget;

  document.body.classList.add('trk-col-resizing');
  handle.classList.add('trk-col-resizer-active');

  function onMove(moveEvent) {
    const width = Math.max(MIN_COL_WIDTH, Math.round(startWidth + (moveEvent.pageX - startX)));
    th.style.width = width + 'px';
    const total = Math.round(startTableWidth + (width - startWidth));
    table.style.width = total + 'px';
    table.style.minWidth = total + 'px';
  }

  function onUp() {
    document.removeEventListener('mousemove', onMove);
    document.removeEventListener('mouseup', onUp);
    document.body.classList.remove('trk-col-resizing');
    handle.classList.remove('trk-col-resizer-active');
    saveWidths(key, measure(table));
  }

  document.addEventListener('mousemove', onMove);
  document.addEventListener('mouseup', onUp);
}

function setup(table, key) {
  const cells = headerCells(table);
  if (!cells.length) {
    return false;
  }

  const saved = loadWidths(key);
  if (saved && saved.length === cells.length) {
    applyWidths(table, saved);
  }

  cells.forEach((th, index) => {
    // The last column absorbs whatever space is left, so it gets no handle.
    if (index === cells.length - 1 || th.querySelector('.trk-col-resizer')) {
      return;
    }

    const handle = document.createElement('span');
    handle.className = 'trk-col-resizer';
    handle.title = 'Drag to resize this column. Double-click to reset all widths.';

    handle.addEventListener('mousedown', (event) => startDrag(event, table, key, index));
    handle.addEventListener('click', (event) => event.stopPropagation());
    handle.addEventListener('dblclick', (event) => {
      event.preventDefault();
      event.stopPropagation();
      clearWidths(key);
      resetWidths(table);
    });

    th.classList.add('trk-col-resizable');
    th.appendChild(handle);
  });

  return true;
}

export default {
  install(Vue) {
    Vue.directive('trk-resizable', {
      inserted(el, binding) {
        const table = el.tagName === 'TABLE' ? el : el.querySelector('table');
        if (!table) {
          return;
        }
        const key = binding.value || '';
        // The header can land a tick later while the first page is still loading.
        if (!setup(table, key)) {
          window.requestAnimationFrame(() => setup(table, key));
        }
      }
    });
  }
};
